import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, BatteryChargingIcon, MagnetIcon, PaletteIcon, RulerIcon, SparklesIcon, WifiOffIcon } from 'lucide-react';
import { CountUp } from '../fx/CountUp';
import { productFeatures } from '../../data/productFeatures';
import type { ProductFeature } from '../../data/productFeatures';
const ICONS: Record<ProductFeature['icon'], React.ElementType> = {
  wifi: WifiOffIcon,
  battery: BatteryChargingIcon,
  palette: PaletteIcon,
  ruler: RulerIcon,
  magnet: MagnetIcon,
  sparkles: SparklesIcon
};
const CYCLE_MS = 3600;
const ease = [0.23, 1, 0.32, 1] as const;

/** Interactive product console: feature list ↔ orbit nodes ↔ live readout, auto-cycling until touched. */
export function ProductReveal() {
  const reduce = useReducedMotion();
  const [active, setActive] = useState(0);
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (touched || reduce) return;
    const id = window.setInterval(() => setActive(a => (a + 1) % productFeatures.length), CYCLE_MS);
    return () => window.clearInterval(id);
  }, [touched, reduce]);

  // Preload all six feature images so switching never flashes or shifts layout.
  useEffect(() => {
    productFeatures.forEach(pf => {
      const img = new Image();
      img.src = pf.image;
    });
  }, []);
  const select = (i: number) => {
    setTouched(true);
    setActive(i);
  };
  const f = productFeatures[active];
  const left = productFeatures.slice(0, 3);
  const right = productFeatures.slice(3);
  return <section aria-labelledby="reveal-title" className="mf-tech-grid relative overflow-hidden bg-deep" data-id="element-297" data-mp="16p88udmao907">
      <div className="relative mx-auto max-w-site px-5 pb-10 pt-10 md:px-8 md:pb-14 md:pt-14" data-id="element-298" data-mp="1bockdawoyitp">
        <div className="text-center" data-id="element-299" data-mp="1gj8bm8u3t25r">
          <p className="font-display text-sm uppercase tracking-[0.3em] text-marine" data-id="element-300" data-mp="1ljwcdm84wxd6">System · M1 PRO</p>
          <h2 id="reveal-title" className="mt-3 font-display text-6xl font-semibold uppercase leading-[0.9] md:text-8xl" data-id="element-301" data-mp="vvh44tdkgvq0">
            Meet the M1 PRO.
          </h2>
          <p className="mt-4 font-display text-xl uppercase tracking-[0.14em] text-ice md:text-2xl" data-id="element-302" data-mp="qvcm2yx0fsxo">
            Professional-Grade Lighting. Reimagined.
          </p>
        </div>

        <div className="mt-8 grid items-center gap-10 lg:grid-cols-[1fr_1.35fr_1fr] lg:gap-12" data-id="element-303" data-mp="10ta8rhz81ho6">
          <FeatureList items={left} offset={0} active={active} onSelect={select} align="right" data-id="element-304" data-mp="1ekx8xswabac8" />

          {/* Product console */}
          <div className="relative mx-auto aspect-square w-full max-w-[540px]" data-id="element-305" data-mp="1oko1vq575ha7">
            <OrbitRings data-id="element-306" data-mp="1oilezezcc49p" />
            {productFeatures.map((pf, i) => {
            const angle = i / productFeatures.length * Math.PI * 2 - Math.PI / 2;
            const x = 50 + Math.cos(angle) * 47;
            const y = 50 + Math.sin(angle) * 47;
            const on = i === active;
            const Icon = ICONS[pf.icon];
            return <button key={pf.id} type="button" onClick={() => select(i)} aria-label={pf.title} aria-pressed={on} className={`absolute z-10 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border transition-[background-color,border-color,color,transform] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ice ${on ? 'scale-110 border-ice bg-marine text-white shadow-[0_0_18px_rgba(36,141,255,0.7)]' : 'border-marine/40 bg-deep text-titanium hover:border-ice hover:text-white'}`} style={{
              left: `${x}%`,
              top: `${y}%`
            }} data-id="element-307" data-mp="20vp5c3w2i3u5" data-mpi={i}>
                  <Icon className="h-5 w-5" aria-hidden data-id="element-308" data-mp="37iaw0fd5tfb2" />
                </button>;
          })}
            <div className="absolute inset-[11%] overflow-hidden rounded-full border border-marine/30 bg-deep" data-id="element-309" data-mp="17guk74b4atyn">
              <AnimatePresence initial={false} data-id="element-310" data-mp="12j2v0i1uxly5">
                <motion.img key={f.id} src={f.image} alt={f.imageAlt} width={1254} height={1254} decoding="async" className="absolute inset-0 h-full w-full object-cover" initial={{
                opacity: 0,
                scale: reduce ? 1 : 0.97
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease
              }} data-id="element-311" data-mp="yg6d6tk55tv9" />
              </AnimatePresence>
              <span className="mf-scan" aria-hidden data-id="element-312" data-mp="apafxgwx26gm" />
            </div>
          </div>

          <FeatureList items={right} offset={3} active={active} onSelect={select} align="left" data-id="element-313" data-mp="5r91j9afqvbh" />
        </div>

        {/* Live readout */}
        <div className="mf-hud mx-auto mt-8 max-w-3xl border border-marine/25 bg-[#06121F]/80 px-6 py-6 backdrop-blur-sm md:px-10" aria-live="polite" data-id="element-314" data-mp="2cgsr4f1jj8ra">
          <AnimatePresence mode="wait" initial={false} data-id="element-315" data-mp="2hdj772dci4cp">
            <motion.div key={f.id} initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -6
          }} transition={{
            duration: 0.2,
            ease
          }} className="grid items-center gap-4 text-center md:grid-cols-[auto_1fr] md:gap-10 md:text-left" data-id="element-316" data-mp="2me776djjxvj2">
              <div className="font-display uppercase" data-id="element-317" data-mp="1ymrxfzpg4bxs">
                <span className="text-6xl font-semibold leading-none text-white md:text-7xl" data-id="element-318" data-mp="22pev4h8ssr2w">
                  <CountUp value={f.metric.value} decimals={f.metric.decimals} data-id="element-319" data-mp="27i81f9jx07h3" />
                </span>
                <span className="ml-2 text-2xl font-semibold tracking-[0.06em] text-ice" data-id="element-320" data-mp="1ay8v00t2utdm">{f.metric.unit}</span>
                <p className="mt-1 text-sm tracking-[0.16em] text-titanium" data-id="element-321" data-mp="1ezkio0se54j6">{f.metric.label}</p>
              </div>
              <div data-id="element-322" data-mp="2i9ygowji1678">
                <h3 className="font-display text-2xl font-semibold uppercase tracking-[0.04em]" data-id="element-323" data-mp="2i9gap871bdm4">{f.title}</h3>
                <p className="mt-1 text-titanium" data-id="element-324" data-mp="2s1ma32zm8xbc">{f.body}</p>
              </div>
            </motion.div>
          </AnimatePresence>
          {/* Cycle progress */}
          <div className="mt-6 flex gap-1.5" aria-hidden data-id="element-325" data-mp="2n3b1swoyyuv8">
            {productFeatures.map((pf, i) => <span key={pf.id} className="h-0.5 flex-1 overflow-hidden bg-white/10" data-id="element-326" data-mp="1zf19dbx938v4" data-mpi={i}>
                {i === active && <motion.span key={`${pf.id}-${touched}`} className="block h-full bg-marine" initial={{
              width: touched || reduce ? '100%' : '0%'
            }} animate={{
              width: '100%'
            }} transition={{
              duration: touched || reduce ? 0 : CYCLE_MS / 1000,
              ease: 'linear'
            }} data-id="element-327" data-mp="28dn1nysiy293" />}
              </span>)}
          </div>
        </div>

        <ul aria-label="Additional specifications" className="mx-auto mt-6 flex max-w-3xl flex-wrap items-center justify-center gap-x-6 gap-y-2 border-y mf-hairline py-4 font-display text-sm font-semibold uppercase tracking-[0.12em] text-titanium" data-id="element-328" data-mp="3n94tl0vz3vbp">
          <li data-id="element-329" data-mp="3ico2bxth90ue">IP68 waterproof rating</li>
          <li aria-hidden className="text-marine" data-id="element-330" data-mp="3idpi2flfubig">◆</li>
          <li data-id="element-331" data-mp="9wundnesnxfg">Remote control included <span className="normal-case tracking-normal text-titanium/60" data-id="element-332" data-mp="9wl5yo7teyld">(2 AAA, not included)</span></li>
          <li aria-hidden className="text-marine" data-id="element-333" data-mp="ewz4nfnj0w6q">◆</li>
          <li data-id="element-334" data-mp="6rt7jvxsdfj">Durable ABS housing</li>
        </ul>

        <div className="mt-8 flex justify-center" data-id="element-335" data-mp="45pjvc62i2l">
          <Link to="/m1-pro" className="inline-flex items-center gap-2 font-display text-base font-semibold uppercase tracking-[0.12em] text-white hover:text-ice" data-id="element-336" data-mp="541i8yfeyn2u">
            Full M1 PRO specifications <ArrowRightIcon className="h-4 w-4" data-id="element-337" data-mp="xmoyo0jgtdxl" />
          </Link>
        </div>
      </div>
    </section>;
}
function OrbitRings() {
  return <svg viewBox="0 0 200 200" className="absolute inset-0 h-full w-full" aria-hidden data-id="element-338" data-mp="2oy24zl66ugov">
      <g className="mf-spin-slow" data-id="element-339" data-mp="2kw8bpxyboeyq">
        <circle cx="100" cy="100" r="94" fill="none" stroke="#248DFF" strokeOpacity="0.35" strokeDasharray="1 4" data-id="element-340" data-mp="2kx9wiu554ftn" />
        {Array.from({
        length: 60
      }).map((_, i) => {
        const a = i / 60 * Math.PI * 2;
        const long = i % 5 === 0;
        return <line key={i} x1={100 + Math.cos(a) * (long ? 86 : 89)} y1={100 + Math.sin(a) * (long ? 86 : 89)} x2={100 + Math.cos(a) * 91} y2={100 + Math.sin(a) * 91} stroke="#9CD8FF" strokeOpacity={long ? 0.5 : 0.2} strokeWidth="0.5" data-id="element-341" data-mp="38m2mm9z2ztgc" data-mpi={i} />;
      })}
      </g>
      <g className="mf-spin-rev" data-id="element-342" data-mp="1n8gbsw7vqzng">
        <circle cx="100" cy="100" r="80" fill="none" stroke="#9CD8FF" strokeOpacity="0.25" strokeDasharray="30 12 4 12" data-id="element-343" data-mp="1n7eyel3e612k" />
      </g>
    </svg>;
}
function FeatureList({
  items,
  offset,
  active,
  onSelect,
  align
}: {
  items: ProductFeature[];
  offset: number;
  active: number;
  onSelect: (i: number) => void;
  align: 'left' | 'right';
}) {
  return <ul className="hidden space-y-2 lg:block" data-id="element-344" data-mp="1rtfnvm5o3u0x">
      {items.map((c, j) => {
      const i = offset + j;
      const on = i === active;
      return <li key={c.id} data-id="element-345" data-mp="1vurdvssgeh9n" data-mpi={j}>
            <button type="button" onClick={() => onSelect(i)} aria-pressed={on} className={`w-full rounded-lg border px-4 py-4 text-left transition-[background-color,border-color] duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ice ${align === 'right' ? 'lg:text-right' : ''} ${on ? 'border-marine/60 bg-marine/10' : 'border-transparent hover:border-white/10 hover:bg-white/[0.03]'}`} data-id="element-346" data-mp="1vxdnrz2th8v1">
              <span className={`block font-display text-2xl font-semibold uppercase tracking-[0.04em] ${on ? 'text-white' : 'text-titanium'}`} data-id="element-347" data-mp="ct0pe1vo7znm">
                {c.title}
              </span>
              <span className={`mt-1 block text-[15px] ${on ? 'text-titanium' : 'text-titanium/60'}`} data-id="element-348" data-mp="ltfdf900rsut">{c.body}</span>
            </button>
          </li>;
    })}
    </ul>;
}